import { UnstyledButton, Box, Indicator, VisuallyHidden } from '@mantine/core';
import {
  IconHome,
  IconEdit,
  IconDroplet,
  IconTool,
  IconFish,
  IconChartDots,
  IconFlask,
  IconContainer,
  IconDevices,
  IconSettings,
  IconUsers,
  IconChecklist,
  IconCpu,
  IconNotebook,
  IconTools,
  IconBolt,
} from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { useTankParameters } from '@/hooks/useTankParameters';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { useInboxCount } from '@/hooks/usePhotoInbox';
import type { TankFeatures } from '@/api/parameters';
import classes from './NavbarSimple.module.css';

interface NavbarSimpleProps {
  onNavigate?: () => void;
}

type FeatureKey = keyof TankFeatures;

interface NavItem {
  path: string;
  label: string;
  icon: typeof IconHome;
  /** Feature flag to check - if undefined, always show */
  featureFlag?: FeatureKey;
  /** Also active on the routes under it (Livestock: its photo inbox and import). */
  nested?: boolean;
}

/**
 * Simple navigation sidebar with feature-based conditional rendering.
 *
 * Nav items can be conditionally shown/hidden based on tank features:
 * - Dosing link only shows when has_doser is true (doser hardware,
 *   manual dosing events, or an active dosing schedule)
 * - Maintenance link only shows when has_maintenance is true
 * - ICP Tests is always visible (the import form lives on that page)
 *
 * Settings and Admin: Users are pinned to the bottom of the sidebar
 * with a visual separator, matching the reference design.
 *
 * Livestock carries the number of photos waiting in the tank's inbox.
 */
export function NavbarSimple({ onNavigate }: NavbarSimpleProps): JSX.Element {
  const location = useLocation();
  const { data: paramConfig } = useTankParameters();
  const { data: notifPrefs } = useNotificationPreferences();
  const { data: photosToSort = 0 } = useInboxCount();

  // Show badge on Settings only after user has dismissed onboarding modal twice
  const showSettingsBadge =
    !!notifPrefs &&
    !notifPrefs.setup_completed &&
    !notifPrefs.enabled &&
    notifPrefs.setup_prompt_count >= 2;

  // Main nav items (feature-gated)
  const mainNavItems: NavItem[] = [
    { path: '/charts', label: 'Home', icon: IconHome },
    { path: '/tasks', label: 'Tasks', icon: IconChecklist },
    { path: '/journal', label: 'Journal', icon: IconNotebook },
    { path: '/analysis', label: 'Analysis', icon: IconChartDots },
    { path: '/icp-tests', label: 'ICP Tests', icon: IconFlask },
    { path: '/power', label: 'Power', icon: IconBolt, featureFlag: 'has_power_monitoring' },
    { path: '/dosing', label: 'Dosing', icon: IconDroplet, featureFlag: 'has_doser' },
    { path: '/equipment', label: 'Equipment', icon: IconCpu },
    { path: '/maintenance', label: 'Maintenance', icon: IconTool, featureFlag: 'has_maintenance' },
    { path: '/livestock', label: 'Livestock', icon: IconFish, nested: true },
    { path: '/tanks', label: 'Tanks', icon: IconContainer },
    { path: '/devices', label: 'Devices', icon: IconDevices },
    { path: '/data-editor', label: 'Data Editor', icon: IconEdit },
    { path: '/tools', label: 'Tools', icon: IconTools },
  ];

  // Bottom-pinned items (Settings, Admin)
  const bottomNavItems: NavItem[] = [
    { path: '/settings', label: 'Settings', icon: IconSettings },
    { path: '/admin/users', label: 'Admin: Users', icon: IconUsers },
  ];

  // Filter nav items based on feature flags
  const visibleMainItems = mainNavItems.filter((item) => {
    if (!item.featureFlag) return true;
    return paramConfig?.features?.[item.featureFlag] ?? false;
  });

  const renderNavLink = (item: NavItem) => {
    const Icon = item.icon;
    const isActive =
      location.pathname === item.path ||
      (item.path === '/charts' && location.pathname === '/') ||
      (!!item.nested && location.pathname.startsWith(`${item.path}/`));
    const isSettings = item.path === '/settings';
    const toSort = item.path === '/livestock' ? photosToSort : 0;
    const navLink = (
      <UnstyledButton
        key={item.path}
        component={Link}
        to={item.path}
        className={`${classes.navLink}${isActive ? ` ${classes.navLinkActive}` : ''}`}
        aria-current={isActive ? 'page' : undefined}
        onClick={onNavigate}
      >
        <Icon size={16} />
        {item.label}
        {toSort > 0 && (
          <VisuallyHidden>{`, ${toSort} ${toSort === 1 ? 'photo' : 'photos'} to sort`}</VisuallyHidden>
        )}
      </UnstyledButton>
    );

    if (toSort > 0) {
      return (
        <Indicator
          key={item.path}
          label={toSort > 99 ? '99+' : toSort}
          size={16}
          color="teal"
          position="middle-end"
          offset={22}
          styles={{ indicator: { fontVariantNumeric: 'tabular-nums' } }}
        >
          {navLink}
        </Indicator>
      );
    }

    if (isSettings) {
      return (
        <Indicator
          key={item.path}
          disabled={!showSettingsBadge}
          color="blue"
          size={8}
          offset={7}
        >
          {navLink}
        </Indicator>
      );
    }

    return navLink;
  };

  return (
    <Box
      h="100%"
      p="md"
      style={{
        backgroundColor: 'var(--rdh-sidebar-bg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-xs)',
      }}
    >
      {/* Main navigation items */}
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--mantine-spacing-xs)' }}>
        {visibleMainItems.map(renderNavLink)}
      </Box>

      {/* Bottom-pinned items with separator */}
      <Box
        style={{
          borderTop: '1px solid rgba(179, 245, 234, 0.15)',
          paddingTop: 'var(--mantine-spacing-sm)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--mantine-spacing-xs)',
        }}
      >
        {bottomNavItems.map(renderNavLink)}
      </Box>
    </Box>
  );
}
